const MAGIC_0 = 83;
const MAGIC_1 = 73;
const MAGIC_2 = 79;
const PROTOCOL_VERSION = 1;

const MESSAGE_JOINED = 1;
const MESSAGE_SNAPSHOT = 2;
const MESSAGE_UPGRADE_OPTIONS = 3;
const MESSAGE_DEFEAT = 4;
const MESSAGE_GAME_END = 5;
const MESSAGE_PONG = 6;
const MESSAGE_ERROR = 7;
const DEFAULT_END_SCREEN_TIMEOUT_MS = 30000;

const ENTITY_FLAG_OWNER = 1 << 0;
const ENTITY_FLAG_DIRECTION = 1 << 1;
const ENTITY_FLAG_NAME = 1 << 2;
const ENTITY_FLAG_COSMETICS = 1 << 3;
const ENTITY_FLAG_HP = 1 << 4;
const ENTITY_FLAG_MAX_HP = 1 << 5;
const ENTITY_FLAG_LEVEL = 1 << 6;
const ENTITY_FLAG_XP = 1 << 7;
const ENTITY_FLAG_XP_IN_LEVEL = 1 << 8;
const ENTITY_FLAG_XP_TO_NEXT_LEVEL = 1 << 9;
const ENTITY_FLAG_WEAPON_KIND = 1 << 10;
const ENTITY_FLAG_HEART_TIER = 1 << 11;
const ENTITY_FLAG_IS_LEADER = 1 << 12;
const ENTITY_FLAG_DAMAGE_FLASH = 1 << 13;

const textDecoder = new TextDecoder();

type EntityStaticCache = {
  owner?: unknown;
  direction?: unknown;
  name?: unknown;
  cosmetics?: unknown;
  max_hp?: unknown;
  level?: unknown;
  weapon_kind?: unknown;
  heart_tier?: unknown;
  xp?: unknown;
};

const entityStaticCache = new Map<string, EntityStaticCache>();
let leaderboardCache: unknown[] = [];

export function parseBinaryServerMessage(data: ArrayBuffer): unknown {
  const reader = new BinaryReader(data);

  if (
    reader.readU8() !== MAGIC_0 ||
    reader.readU8() !== MAGIC_1 ||
    reader.readU8() !== MAGIC_2 ||
    reader.readU8() !== PROTOCOL_VERSION
  ) {
    throw new Error("Invalid binary protocol header");
  }

  const tag = reader.readU8();

  switch (tag) {
    case MESSAGE_JOINED: {
      clearProtocolCache();

      const playerId = reader.readId();
      const snapshot = readSnapshot(reader, true);

      return {
        Joined: {
          player_id: playerId,
          snapshot
        }
      };
    }

    case MESSAGE_SNAPSHOT:
      return {
        Snapshot: readSnapshot(reader, false)
      };

    case MESSAGE_UPGRADE_OPTIONS: {
      const timeoutMs = reader.readU32();
      const count = reader.readU32();
      const options: unknown[] = [];

      for (let index = 0; index < count; index += 1) {
        options.push(readUpgrade(reader));
      }

      return {
        UpgradeOptions: {
          options,
          timeout_ms: timeoutMs
        }
      };
    }

    case MESSAGE_DEFEAT:
      return {
        Defeat: {
          timeout_ms: reader.remainingBytes() >= 4 ? reader.readU32() : DEFAULT_END_SCREEN_TIMEOUT_MS
        }
      };

    case MESSAGE_GAME_END:
      clearProtocolCache();

      return {
        GameEnd: {
          winner_player_id: reader.readId(),
          won: reader.readBool(),
          timeout_ms: reader.remainingBytes() >= 4 ? reader.readU32() : DEFAULT_END_SCREEN_TIMEOUT_MS
        }
      };

    case MESSAGE_PONG:
      return {
        Pong: {
          client_time_ms: reader.readU64Number(),
          server_tick: reader.readU64Number()
        }
      };

    case MESSAGE_ERROR:
      return {
        Error: {
          code: reader.readString(),
          message: reader.readString()
        }
      };

    default:
      throw new Error("Unknown binary message tag");
  }
}

function readSnapshot(reader: BinaryReader, forceFull: boolean): Record<string, unknown> {
  const tick = reader.readU64Number();
  const hasLocalPlayerId = reader.readBool();
  const localPlayerId = hasLocalPlayerId ? reader.readId() : null;
  const arenaWidth = reader.readF32();
  const arenaHeight = reader.readF32();
  const defeated = reader.readBool();
  const hasLeader = reader.readBool();
  const leaderPlayerId = hasLeader ? reader.readId() : null;
  const leaderPosition = hasLeader ? reader.readVec2() : null;
  const leaderLevel = hasLeader ? reader.readU32() : null;
  const entityCount = reader.readU32();
  const entities: Record<string, unknown>[] = [];
  const currentKeys = new Set<string>();

  for (let index = 0; index < entityCount; index += 1) {
    const entity = readEntity(reader);
    const key = entityCacheKey(entity);

    currentKeys.add(key);
    hydrateEntity(entity, key);
    entities.push(entity);
  }

  pruneEntityCache(currentKeys);

  const leaderboardCount = reader.readU32();
  const leaderboard: unknown[] = [];

  for (let index = 0; index < leaderboardCount; index += 1) {
    leaderboard.push(readLeaderboardEntry(reader));
  }

  if (forceFull || leaderboard.length > 0) {
    leaderboardCache = leaderboard;
  }

  const pendingUpgradeCount = reader.readU32();
  const pendingUpgrades: unknown[] = [];

  for (let index = 0; index < pendingUpgradeCount; index += 1) {
    pendingUpgrades.push(readUpgrade(reader));
  }

  return {
    tick,
    local_player_id: localPlayerId,
    arena_width: arenaWidth,
    arena_height: arenaHeight,
    leader_player_id: leaderPlayerId,
    leader_position: leaderPosition,
    leader_level: leaderLevel,
    entities,
    leaderboard: leaderboard.length > 0 ? leaderboard : leaderboardCache,
    pending_upgrades: pendingUpgrades,
    defeated
  };
}

function readEntity(reader: BinaryReader): Record<string, unknown> {
  const id = reader.readId();
  const flags = reader.readU32();
  const kind = readEntityKind(reader);
  const position = reader.readVec2();
  const radius = reader.readF32();
  const entity: Record<string, unknown> = {
    id,
    kind,
    position,
    radius
  };

  if (hasFlag(flags, ENTITY_FLAG_OWNER)) {
    entity.owner = reader.readId();
  }

  if (hasFlag(flags, ENTITY_FLAG_DIRECTION)) {
    entity.direction = reader.readVec2();
  }

  if (hasFlag(flags, ENTITY_FLAG_NAME)) {
    entity.name = reader.readString();
  }

  if (hasFlag(flags, ENTITY_FLAG_COSMETICS)) {
    entity.cosmetics = {
      ball_color: reader.readRgbColor(),
      name_color: reader.readRgbColor()
    };
  }

  if (hasFlag(flags, ENTITY_FLAG_HP)) {
    entity.hp = reader.readF32();
  }

  if (hasFlag(flags, ENTITY_FLAG_MAX_HP)) {
    entity.max_hp = reader.readF32();
  }

  if (hasFlag(flags, ENTITY_FLAG_LEVEL)) {
    entity.level = reader.readU32();
  }

  if (hasFlag(flags, ENTITY_FLAG_XP)) {
    entity.xp = reader.readU32();
  }

  if (hasFlag(flags, ENTITY_FLAG_XP_IN_LEVEL)) {
    entity.xp_in_level = reader.readU32();
  }

  if (hasFlag(flags, ENTITY_FLAG_XP_TO_NEXT_LEVEL)) {
    entity.xp_to_next_level = reader.readU32();
  }

  if (hasFlag(flags, ENTITY_FLAG_WEAPON_KIND)) {
    entity.weapon_kind = readWeaponKind(reader);
  }

  if (hasFlag(flags, ENTITY_FLAG_HEART_TIER)) {
    entity.heart_tier = readHeartTier(reader);
  }

  if (hasFlag(flags, ENTITY_FLAG_IS_LEADER)) {
    entity.is_leader = true;
  }

  if (hasFlag(flags, ENTITY_FLAG_DAMAGE_FLASH)) {
    entity.damage_flash = true;
  }

  return entity;
}

function hydrateEntity(entity: Record<string, unknown>, key: string): void {
  const cached = entityStaticCache.get(key) ?? {};

  mergeCachedField(entity, cached, "owner");
  mergeCachedField(entity, cached, "direction");
  mergeCachedField(entity, cached, "name");
  mergeCachedField(entity, cached, "cosmetics");
  mergeCachedField(entity, cached, "max_hp");
  mergeCachedField(entity, cached, "level");
  mergeCachedField(entity, cached, "weapon_kind");
  mergeCachedField(entity, cached, "heart_tier");

  if (entity.kind === "Heart") {
    mergeCachedField(entity, cached, "xp");
  }

  entityStaticCache.set(key, cached);
}

function mergeCachedField(
  entity: Record<string, unknown>,
  cached: EntityStaticCache,
  field: keyof EntityStaticCache
): void {
  if (entity[field] === undefined) {
    if (cached[field] !== undefined) {
      entity[field] = cloneCachedValue(cached[field]);
    }

    return;
  }

  cached[field] = cloneCachedValue(entity[field]);
}

function cloneCachedValue(value: unknown): unknown {
  if (!value || typeof value !== "object") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(cloneCachedValue);
  }

  return {
    ...(value as Record<string, unknown>)
  };
}

function pruneEntityCache(currentKeys: Set<string>): void {
  for (const key of entityStaticCache.keys()) {
    if (!currentKeys.has(key)) {
      entityStaticCache.delete(key);
    }
  }
}

function entityCacheKey(entity: Record<string, unknown>): string {
  return `${String(entity.kind)}:${String(entity.id)}`;
}

function clearProtocolCache(): void {
  entityStaticCache.clear();
  leaderboardCache = [];
}

function readLeaderboardEntry(reader: BinaryReader): Record<string, unknown> {
  return {
    player_id: reader.readId(),
    name: reader.readString(),
    level: reader.readU32(),
    xp: reader.readU32(),
    score: reader.readU32(),
    is_bot: reader.readBool(),
    is_leader: reader.readBool()
  };
}

function readUpgrade(reader: BinaryReader): unknown {
  const tag = reader.readU8();

  switch (tag) {
    case 1:
      return {
        Stat: readStatUpgradeKind(reader)
      };

    case 2:
      return {
        NewWeapon: readWeaponKind(reader)
      };

    case 3:
      return {
        WeaponUpgrade: {
          weapon: readWeaponKind(reader),
          upgrade: readWeaponUpgradeKind(reader)
        }
      };

    default:
      throw new Error("Unknown upgrade tag");
  }
}

function readEntityKind(reader: BinaryReader): string {
  const tag = reader.readU8();

  switch (tag) {
    case 1:
      return "Player";
    case 2:
      return "Bot";
    case 3:
      return "Zombie";
    case 4:
      return "Projectile";
    case 5:
      return "Heart";
    case 6:
      return "DamageZone";
    case 7:
      return "OrbitingBall";
    default:
      throw new Error("Unknown entity kind");
  }
}

function readWeaponKind(reader: BinaryReader): string {
  const tag = reader.readU8();

  switch (tag) {
    case 1:
      return "BasicShot";
    case 2:
      return "OrbitingBalls";
    case 3:
      return "DamageZone";
    default:
      throw new Error("Unknown weapon kind");
  }
}

function readHeartTier(reader: BinaryReader): string {
  const tag = reader.readU8();

  switch (tag) {
    case 1:
      return "Red";
    case 2:
      return "Brown";
    case 3:
      return "Silver";
    case 4:
      return "Gold";
    case 5:
      return "PlatinumBlue";
    default:
      throw new Error("Unknown heart tier");
  }
}

function readStatUpgradeKind(reader: BinaryReader): string {
  const tag = reader.readU8();

  switch (tag) {
    case 1:
      return "MaxHp";
    case 2:
      return "Speed";
    default:
      throw new Error("Unknown stat upgrade kind");
  }
}

function readWeaponUpgradeKind(reader: BinaryReader): string {
  const tag = reader.readU8();

  switch (tag) {
    case 1:
      return "Frequency";
    case 2:
      return "Range";
    case 3:
      return "ProjectileCount";
    case 4:
      return "ProjectileSpeed";
    case 5:
      return "OrbitalSpeed";
    case 6:
      return "Damage";
    default:
      throw new Error("Unknown weapon upgrade kind");
  }
}

function hasFlag(flags: number, flag: number): boolean {
  return (flags & flag) !== 0;
}

class BinaryReader {
  private readonly view: DataView;
  private offset = 0;

  public constructor(private readonly buffer: ArrayBuffer) {
    this.view = new DataView(buffer);
  }

  public readBool(): boolean {
    return this.readU8() !== 0;
  }

  public readU8(): number {
    this.ensureAvailable(1);
    const value = this.view.getUint8(this.offset);
    this.offset += 1;
    return value;
  }

  public readU32(): number {
    this.ensureAvailable(4);
    const value = this.view.getUint32(this.offset, true);
    this.offset += 4;
    return value;
  }

  public readU64Number(): number {
    const value = this.readU64BigInt();

    if (value <= BigInt(Number.MAX_SAFE_INTEGER)) {
      return Number(value);
    }

    return Number.MAX_SAFE_INTEGER;
  }

  public readId(): number | string {
    const value = this.readU64BigInt();

    if (value <= BigInt(Number.MAX_SAFE_INTEGER)) {
      return Number(value);
    }

    return value.toString();
  }

  public readF32(): number {
    this.ensureAvailable(4);
    const value = this.view.getFloat32(this.offset, true);
    this.offset += 4;
    return value;
  }

  public readString(): string {
    const length = this.readU32();
    this.ensureAvailable(length);

    const bytes = new Uint8Array(this.buffer, this.offset, length);
    this.offset += length;

    return textDecoder.decode(bytes);
  }

  public readVec2(): { x: number; y: number } {
    return {
      x: this.readF32(),
      y: this.readF32()
    };
  }

  public readRgbColor(): { r: number; g: number; b: number } {
    return {
      r: this.readU8(),
      g: this.readU8(),
      b: this.readU8()
    };
  }

  public remainingBytes(): number {
    return this.buffer.byteLength - this.offset;
  }

  private readU64BigInt(): bigint {
    this.ensureAvailable(8);
    const value = this.view.getBigUint64(this.offset, true);
    this.offset += 8;
    return value;
  }

  private ensureAvailable(byteCount: number): void {
    if (this.offset + byteCount > this.buffer.byteLength) {
      throw new Error("Binary message ended unexpectedly");
    }
  }
}